import {
  ChangeEvent,
  useState,
  useEffect,
  useRef,
  KeyboardEvent,
  Fragment,
} from "react";
import { BiSearch } from "react-icons/bi";
import { Flex, Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilState } from "recoil";

import { NoResults } from "../NoResults";
import { DropdownItem } from "../DropdownItem";
import { useUserSuggestionsForSearch } from "MVVM/Hooks/useSuggestedUsers";
import StackedAvatars from "../StackedAvatars";
import { AllstarSearchSuggestionsPreview } from "MVVM/State";
import { getRecentSearches } from "MVVM/Utilities/RecentSearches";
import { URLS } from "MVVM/Utilities/Maps";
import { ProContentLink } from "MVVM/Components/ProContentLink";

const STEAMID_HELP_URL = "https://steamdb.info/calculator/";

/**
 * The compact variant's empty state is recents only, capped. Focusing the field
 * used to list every recent search plus the whole suggestions set, which the
 * old 250px clip hid behind a scrollbar. Suggested users are off here for now;
 * they still appear in the full-size bar and still back the local filter once
 * you start typing.
 */
const COMPACT_RECENT_LIMIT = 3;

export interface ISearchItem {
  id: string;
  name: string;
  image: string;
  gameId?: string;
  reason?: string;
  userGroup?: {
    type: string;
  };
  rank?: number;
  steam?: {
    steamid?: string;
    personaname?: string;
  };
  isProPlayer?: boolean;
  isSteamLookup?: boolean;
  game?: string;
}

const NavSearchBar = ({
  items,
  loading,
  onSelect,
  onFocus,
  debouncedSearch,
  placeholder,
  showSteamIdHint,
  hideSuggestionsPreview,
}: {
  items: ISearchItem[];
  loading: boolean;
  onSelect: (item: ISearchItem) => void;
  onFocus: () => void;
  debouncedSearch: (query: string) => void;
  placeholder: string;
  showSteamIdHint?: boolean;
  // Opt-in: the new site nav has no room for the stacked preview avatars.
  hideSuggestionsPreview?: boolean;
}) => {
  const { suggestedUsers } = useUserSuggestionsForSearch();
  const [recentSearches, setRecentSearches] = useState<ISearchItem[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debouncedSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isServerSearching = inputValue.trim().length >= 3;
  const query = inputValue.trim().toLowerCase();

  const [searchSuggestionsPreview, setSearchSuggestionsPreview] =
    useRecoilState(AllstarSearchSuggestionsPreview);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setInputValue("");
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debouncedSearchTimeoutRef.current) {
        clearTimeout(debouncedSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleSelect = (item: ISearchItem) => {
    onSelect(item);
    setIsOpen(false);
    setShowMobile(false);
    setInputValue("");
    setSelectedIndex(-1);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setInputValue(value);
    setIsOpen(true);
    setSelectedIndex(-1);

    if (value.trim().length >= 3) {
      debouncedSearch(value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const searchResults = getSearchResults();

    if (!isOpen || !searchResults?.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev < searchResults.length - 1 ? prev + 1 : 0;
          scrollToSelectedItem(newIndex);
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : searchResults.length - 1;
          scrollToSelectedItem(newIndex);
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleSelect(searchResults[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleFocus = () => {
    onFocus();
    setIsOpen(true);
    setSelectedIndex(-1);
    setRecentSearches(getRecentSearches());
    inputRef.current && inputRef.current.focus();

    setSearchSuggestionsPreview({
      seenUserIds: previewUsers().map((u) => u.id),
    });
  };

  const setMobileFocus = () => {
    setShowMobile(true);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const getSections = (): { title?: string; items: ISearchItem[] }[] => {
    if (!query) {
      const recentIds = new Set(recentSearches.map((r) => r.id));
      const suggestions = suggestedUsers.filter((u) => !recentIds.has(u.id));
      const recent = hideSuggestionsPreview
        ? recentSearches.slice(0, COMPACT_RECENT_LIMIT)
        : recentSearches;
      const sections: { title?: string; items: ISearchItem[] }[] = [];
      if (recent.length) {
        // Renamed in the nav only; the full-size bar keeps its own wording.
        sections.push({
          title: hideSuggestionsPreview ? "Recently Visited" : "Recent",
          items: recent,
        });
      }
      if (!hideSuggestionsPreview && suggestions.length) {
        sections.push({ items: suggestions });
      }
      return sections;
    }

    if (!isServerSearching) {
      return [
        {
          items: suggestedUsers.filter((user) =>
            user.name.toLowerCase().includes(query),
          ),
        },
      ];
    }

    return [{ items }];
  };

  const getSearchResults = (): ISearchItem[] =>
    getSections().flatMap((section) => section.items);

  const scrollToSelectedItem = (index: number) => {
    const dropdownItems = dropdownRef.current?.querySelectorAll(
      "[data-dropdown-item]",
    );
    if (dropdownItems && dropdownItems[index]) {
      dropdownItems[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  // These are just the first X users with an avatar
  const previewUsers = () => {
    return suggestedUsers.filter((user) => user.image).slice(0, 3);
  };

  // These are the preview users we have not seen yet
  const unseenPreviewUsers = () => {
    const seenUserIds = searchSuggestionsPreview?.seenUserIds;
    return previewUsers().filter((p) => !seenUserIds?.includes(p.id));
  };

  return (
    <>
      <MobileStyledSearch onClick={setMobileFocus} />
      <Container ref={wrapperRef} showMobile={showMobile}>
        <Flex alignItems="center" style={{ gap: 16 }}>
          <InputWrapper $compact={hideSuggestionsPreview} $open={isOpen}>
            <Flex alignItems="center" style={{ flex: 1, minWidth: 0 }}>
              <StyledSearch />
              <StyledInput
                onChange={handleInputChange}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                placeholder={placeholder || "Search..."}
                ref={inputRef}
                type="text"
                value={inputValue}
              />
            </Flex>
            {!hideSuggestionsPreview && (
              <StackedAvatars
                users={unseenPreviewUsers()}
                visible={!isOpen}
                onClick={handleFocus}
              />
            )}
          </InputWrapper>
          <Text
            color="envy"
            display={["block", "block", "none"]}
            fontWeight={500}
            mr={2}
            onClick={() => setShowMobile(false)}
            style={{ zIndex: "1000" }}
          >
            Cancel
          </Text>
        </Flex>
        <SearchContainer isOpen={isOpen} $compact={hideSuggestionsPreview}>
          <InputPlaceholder $compact={hideSuggestionsPreview} />
          <DropdownWrapper
            isVisible={isOpen}
            ref={dropdownRef}
            $compact={hideSuggestionsPreview}
          >
            {showSteamIdHint && (
              <SteamIdHint
                $compact={hideSuggestionsPreview}
                href={STEAMID_HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                What&apos;s my SteamID?
              </SteamIdHint>
            )}
            {(() => {
              const sections = getSections();
              const hasResults = sections.some((s) => s.items.length);

              if (!hasResults) {
                return <NoResults loading={isServerSearching && loading} />;
              }

              const hasCsPro = sections.some((s) =>
                s.items.some(
                  (item: ISearchItem) =>
                    item.isProPlayer && item.game === "CS2",
                ),
              );
              let runningIndex = -1;
              const rows = sections.map((section, sectionIdx) => (
                <Fragment key={section.title ?? `section-${sectionIdx}`}>
                  {section.title && (
                    <SectionTitle>{section.title}</SectionTitle>
                  )}
                  {section.items.map((item: ISearchItem) => {
                    runningIndex += 1;
                    const itemIndex = runningIndex;
                    return (
                      <DropdownItem
                        key={item.id}
                        avatar={item.image}
                        username={item.name}
                        callback={() => handleSelect(item)}
                        reason={item.reason}
                        userGroup={item.userGroup}
                        rank={item.rank}
                        steam={item.steam}
                        isSelected={itemIndex === selectedIndex}
                        isProPlayer={item.isProPlayer}
                        game={item.game}
                      />
                    );
                  })}
                </Fragment>
              ));
              return (
                <>
                  {rows}
                  {hasCsPro && (
                    <ProContentSlot>
                      <ProContentLink
                        label="Top Players"
                        to={URLS.TOP_PLAYERS}
                        onClick={() => setIsOpen(false)}
                      />
                    </ProContentSlot>
                  )}
                </>
              );
            })()}
          </DropdownWrapper>
        </SearchContainer>
      </Container>
    </>
  );
};

const Container = styled(Box)<{ showMobile: boolean }>`
  cursor: pointer !important;
  position: relative;
  width: 100%;
  z-index: 5;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    background-color: ${({ theme }) => theme.colors.midnight};
    height: 100%;
    left: 0;
    padding: 20px 16px;
    position: absolute;
    top: 0;
    width: 100%;

    display: ${(props) => (props.showMobile ? "block" : "none")};
  }
`;

/**
 * Reserves the input's height inside the dropdown, because the full-size panel
 * wraps around the input rather than hanging below it.
 *
 * The compact variant does hang below, so there is nothing to reserve and this
 * would just be empty space above the results.
 */
const InputPlaceholder = styled(Box)<{ $compact?: boolean }>`
  display: ${({ $compact }) => ($compact ? "none" : "block")};
  height: 42px;
  margin-bottom: 10px;
`;

const SteamIdHint = styled.a<{ $compact?: boolean }>`
  /* The compact panel keeps a single rule, the one under the input, so this
     one goes there. The full-size bar is unchanged. */
  border-bottom: ${({ theme, $compact }) =>
    $compact ? "none" : `1px solid ${theme.colors.chalkAlpha08}`};
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  display: block;
  font-size: 13px;
  margin-bottom: 4px;
  padding: 8px 16px 10px;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const SearchContainer = styled(Box)<{ isOpen: boolean; $compact?: boolean }>`
  /* Matches the field exactly. The field is chalkAlpha06 over the strip's
     navy500, so the panel needs both layers rather than one solid value, or it
     reads as a slightly different shade sitting under the input. */
  background-color: ${({ theme, isOpen, $compact }) =>
    isOpen
      ? $compact
        ? theme.colors.navy500
        : theme.colors.navy200
      : "transparent"};
  background-image: ${({ theme, isOpen, $compact }) =>
    isOpen && $compact
      ? `linear-gradient(${theme.colors.chalkAlpha06}, ${theme.colors.chalkAlpha06})`
      : "none"};
  border: 1px solid
    ${({ theme, isOpen }) =>
      isOpen ? theme.colors.chalkAlpha10 : "transparent"};
  /* Square across the top so it meets the field with no seam. */
  border-radius: ${({ $compact }) => ($compact ? "0 0 10px 10px" : "14px")};
  box-shadow: ${({ isOpen }) =>
    isOpen ? "0 12px 32px rgba(0, 0, 0, 0.35)" : "none"};
  left: 0;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  padding: ${({ isOpen, $compact }) =>
    isOpen ? ($compact ? "6px" : "10px") : "0"};
  position: absolute;
  /* Flush under the field rather than wrapping around it. In the strip the top
     border is the only one kept: it is the divider between field and results,
     and the panel takes its shape from the field above rather than from an
     outline of its own. */
  ${({ $compact }) =>
    $compact &&
    `
      border-bottom-width: 0;
      border-left-width: 0;
      border-right-width: 0;
    `}
  top: ${({ $compact }) => ($compact ? "100%" : "-12px")};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  width: 100%;
  z-index: 999;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    box-shadow: none;
    opacity: 1;
    top: 12px;
    visibility: visible;
    width: 100vw;
  }
`;

const InputWrapper = styled(Flex)<{ $compact?: boolean; $open?: boolean }>`
  align-items: center;
  background-color: ${({ theme, $compact }) =>
    $compact ? theme.colors.chalkAlpha06 : theme.colors.navy300};
  border-radius: ${({ $compact }) => ($compact ? "8px" : "12px")};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  height: ${({ $compact }) => ($compact ? "34px" : "42px")};
  justify-content: left;
  margin: 0 auto;
  padding: 3px 6px;
  position: relative;
  width: ${({ $compact }) => ($compact ? "100%" : "96%")};
  z-index: 1000;
  ${({ $compact, theme }) =>
    $compact &&
    `
      border-color: transparent;

      svg {
        height: 18px;
        width: 18px;
      }

      input {
        font-size: 14px;

        &::placeholder {
          color: ${theme.colors.chalkAlpha40};
          font-weight: 400;
        }
      }
    `}

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    cursor: text;
    background-color: ${({ theme }) => theme.colors.navy200};
  }

  /* The compact field becomes the dropdown's header when open, so it squares
     its bottom, matches the panel's colour and drops the accent ring: a green
     outline around the top half of one continuous surface read as an error. */
  ${({ $compact, $open, theme }) =>
    $compact
      ? $open &&
        `
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        `
      : `
          &:focus-within {
            border: 1px solid ${theme.colors.envy};
            box-shadow: 0 0 0 3px rgba(77, 189, 148, 0.12);
          }
        `}
`;

const StyledSearch = styled(BiSearch)`
  border-radius: 20px;
  fill: ${({ theme }) => theme.colors.chalk};
  height: 22px;
  padding: 0 0 0 8px;
  width: 22px;
`;

const MobileStyledSearch = styled(BiSearch)`
  display: none;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    display: block;
    fill: ${({ theme }) => theme.colors.chalk};
    height: 24px;
    margin-left: auto;
    margin-right: 18px;
    padding: 0 0 0 8px;
    width: 24px;
  }
`;

const StyledInput = styled.input`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 500;
  height: 100%;
  outline: none;
  padding-left: 12px;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha60};
  }
`;

const ProContentSlot = styled(Box)`
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  margin-top: 6px;
  padding: 12px;
`;

const SectionTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  padding: 8px 16px 4px;
  text-transform: uppercase;
`;

const DropdownWrapper = styled(Box)<{ isVisible: boolean; $compact?: boolean }>`
  background-color: transparent;
  border-radius: 12px;
  display: ${({ isVisible }) => (isVisible ? "block" : "none")};
  height: ${({ isVisible }) => (isVisible ? "auto" : 0)};
  /* The strip's panel hangs free below the bar, so it can be as tall as it
     needs to be. Clipping at 250px meant a scrollbar inside a dropdown that
     only ever holds a dozen rows. */
  max-height: ${({ $compact }) => ($compact ? "none" : "250px")};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  overflow-y: ${({ $compact }) => ($compact ? "visible" : "auto")};
  padding-right: ${({ $compact }) => ($compact ? "0" : "8px")};
  transition: opacity 0.2s;
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    display: none;
    position: absolute;
    right: 0px;
    top: 0px;
    width: 12px;
    height: 100%;
    z-index: 999;
    background: #0e1920;
    border-left: 1px solid #162029;
  }

  ::-webkit-scrollbar-thumb {
    position: relative;
    display: block;
    height: 20px;
    width: 10px;
    border-radius: 4px;
    margin-left: 2px;
    background-color: #1a2933;
    cursor: pointer;
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    max-height: 100vh;
  }
`;

export { NavSearchBar };
